import { Component, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  CaptureSettingsMerchantTreeDto,
  CaptureValueType,
  LiveBasket_BusinessDetailsTypes,
  LiveBasket_ColorBackgroundTypes,
  LiveBasket_DataMappingTypes
} from '@up/data';
import { SHOPPING_BASKET_SAMPLE_DATA } from './live-basket.const';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CaptureSettingsMerchantApiService } from "../../services/capture-settings-merchant.api.service";
import { ActivatedRoute } from "@angular/router";

export interface LiveBasketConfig {
  businessDetails: {
    showLogo: {
      show: boolean;
      logoUrl: string;
    };
    showBackground: {
      show: boolean;
      color: string;
    };
    showMerchantName: boolean;
    showMerchantAddress: boolean;
    showMerchantTaxIdVat: boolean;
    showCurrentTime: boolean;
    showCurrentDate: boolean;
    showSalesPersonDetails: boolean;
    showTillNumber: boolean;
    showSalesNumber: boolean;
    showWebsite: boolean;
  };
  liveBasketDataMapping: {
    showProductName: boolean;
    showProductQuantity: boolean;
    showUnitPrice: boolean;
    showSubtotalTotal: boolean;
    showTax: boolean;
    showDiscountsPromotions: boolean;
    showOrderReference: boolean;
  };
}

@Component({
  selector: 'up-live-cart',
  templateUrl: './live-cart.component.html',
  styleUrls: [ './live-cart.component.scss' ]
})
export class LiveCartComponent implements OnInit {
  liveBasketSampleProducts = SHOPPING_BASKET_SAMPLE_DATA;

  vatPercent = 6.5;
  subTotal = 0;
  vatValue = 0;
  total = 0;

  _configuration: LiveBasketConfig = {
    businessDetails: {
      showLogo: {
        show: true,
        logoUrl: 'https://upcoimages.s3.eu-west-1.amazonaws.com/images/product-image/1757063473100%20-%20logo-buy-n-large-2-3x.png'
      },
      showBackground: {
        show: true,
        color: '#000'
      },
      showMerchantName: true,
      showMerchantAddress: true,
      showMerchantTaxIdVat: true,
      showCurrentTime: true,
      showCurrentDate: true,
      showSalesPersonDetails: true,
      showTillNumber: true,
      showSalesNumber: true,
      showWebsite: true
    },
    liveBasketDataMapping: {
      showProductName: true,
      showProductQuantity: true,
      showUnitPrice: true,
      showSubtotalTotal: true,
      showTax: true,
      showDiscountsPromotions: true,
      showOrderReference: true,
    }
  };

  processedConfiguration$ = new BehaviorSubject<LiveBasketConfig>(this._configuration);
  public merchantLink: string;
  public orderLink: string;

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private settingsMerchantService: CaptureSettingsMerchantApiService,
  ) {
    const params = this.route.snapshot.queryParams;
    this.orderLink = params.orderLink;
    this.merchantLink = params.merchantLink;
  }

  ngOnInit(): void {
    this.subTotal = this.getSubTotal();
    this.vatValue = this.getVat(this.vatPercent);
    this.total = this.getTotal();

    this.settingsMerchantService.getCoreFunctionalitySettingsTreeByMerchantIdentifier(this.merchantLink, 'customer-connect')
        .subscribe(c => {
          if (!c) {
            return;
          }
          const config = (c as CaptureSettingsMerchantTreeDto[]);
          // preselect first item of the first level and second level
          if (config.length) {
            if (config[0].children.length) {
              const selectedItemLevel1 = config[0].children[1];
              this.processConfig(selectedItemLevel1);
              this.processedConfiguration$.next(this._configuration);
            }
          }
        });
  }

  getSubTotal(): number {
    return this.liveBasketSampleProducts.reduce((acc, item) => acc + item.total, 0);
  }

  getVat(_vatPercent = this.vatPercent): number {
    return this.getSubTotal() * _vatPercent / 100;
  }

  getTotal(): number {
    return this.getSubTotal() + this.getVat(this.vatPercent);
  }

  processConfig(config: CaptureSettingsMerchantTreeDto) {
    const flattedConfig = this.flattenSettingsTree<CaptureSettingsMerchantTreeDto>(config.children);

    flattedConfig.forEach(node => {
      switch (node.key) {

        /** BUSINESS DETAILS */

        case LiveBasket_BusinessDetailsTypes.SHOW_LOGO:
          this._configuration.businessDetails.showLogo.show = this.getTypedValue(node);
          this._configuration.businessDetails.showLogo.logoUrl = this.getLogoUrl(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_BACKGROUND:
          this._configuration.businessDetails.showBackground.show = this.getTypedValue(node);
          this._configuration.businessDetails.showBackground.color = this.getColorCode(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_MERCHANT_NAME:
          this._configuration.businessDetails.showMerchantName = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_MERCHANT_ADDRESS:
          this._configuration.businessDetails.showMerchantAddress = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_MERCHANT_TAX_ID_VAT:
          this._configuration.businessDetails.showMerchantTaxIdVat = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_CURRENT_TIME:
          this._configuration.businessDetails.showCurrentTime = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_CURRENT_DATE:
          this._configuration.businessDetails.showCurrentDate = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_SALES_PERSON_DETAILS:
          this._configuration.businessDetails.showSalesPersonDetails = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_TILL_NUMBER:
          this._configuration.businessDetails.showTillNumber = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_SALES_PERSON:
          this._configuration.businessDetails.showSalesPersonDetails = this.getTypedValue(node);
          break;

        case LiveBasket_BusinessDetailsTypes.SHOW_WEBSITE:
          this._configuration.businessDetails.showWebsite = this.getTypedValue(node);
          break;

        /** LIVE BASKET DATA MAPPING */

        case LiveBasket_DataMappingTypes.PRODUCT_NAME:
          this._configuration.liveBasketDataMapping.showProductName = this.getTypedValue(node);
          break;

        case LiveBasket_DataMappingTypes.PRODUCT_QUANTITY:
          this._configuration.liveBasketDataMapping.showProductQuantity = this.getTypedValue(node);
          break;

        case LiveBasket_DataMappingTypes.UNIT_PRICE:
          this._configuration.liveBasketDataMapping.showUnitPrice = this.getTypedValue(node);
          break;

        case LiveBasket_DataMappingTypes.SUBTOTAL_TOTAL:
          this._configuration.liveBasketDataMapping.showSubtotalTotal = this.getTypedValue(node);
          break;

        case LiveBasket_DataMappingTypes.TAX:
          this._configuration.liveBasketDataMapping.showTax = this.getTypedValue(node);
          break;

        case LiveBasket_DataMappingTypes.DISCOUNTS_PROMOTIONS:
          this._configuration.liveBasketDataMapping.showDiscountsPromotions = this.getTypedValue(node);
          break;

        case LiveBasket_DataMappingTypes.ORDER_REFERENCE:
          this._configuration.liveBasketDataMapping.showOrderReference = this.getTypedValue(node);
          break;

      }
    })
  }

  getTypedValue(node: CaptureSettingsMerchantTreeDto): any {
    const nodeValue = node.merchantSettingValue ?? node.defaultValue;
    if (node.valueType === CaptureValueType.BOOLEAN) {
      if (typeof nodeValue === 'string') {
        return nodeValue.toLowerCase() === 'true' || nodeValue === '1';
      }
      return Boolean(nodeValue);
    }
    return nodeValue;
  }

  getLogoUrl(node: CaptureSettingsMerchantTreeDto) {
    const val = node.children?.find(c => c.key === LiveBasket_ColorBackgroundTypes.BACKGROUND_IMAGE_URL);
    return val?.merchantSettingValue ?? val?.defaultValue;
  }

  getColorCode(node: CaptureSettingsMerchantTreeDto) {
    const val = node.children?.find(c => c.key === LiveBasket_ColorBackgroundTypes.BACKGROUND_COLOR);
    return val?.merchantSettingValue ?? val?.defaultValue;
  }

  getSafeLink(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  flattenSettingsTree<T>(nodes: T[]): T[] {
    const flat: T[] = [];
    const stack: T[] = [ ...nodes ];

    while (stack.length > 0) {
      const node = stack.pop();
      if (node) {
        flat.push(node);
        if (node['children'] && node['children'].length > 0) {
          stack.push(...node['children']);
        }
      }
    }
    return flat;
  }
}
